import { useEffect } from 'react';
import { appServicesAPI } from '../services/api';

const UNSETTLED = ['provisioning', 'pending'];

/**
 * Poll a freshly created App Service until its background apply lands.
 *
 * create() answers before `kubectl apply` runs, so the detail page a launch redirects to
 * first sees "provisioning" — and, loaded only once, never learned the apply FAILED (e.g.
 * Kubernetes rejecting the YAML). This re-reads the stored row (live=false: DB only, no
 * kubectl) every 2s until the status is running/failed, then stops. Bounded at ~60s,
 * which covers the ~20s a failed apply was measured to take.
 *
 * `onUpdate(row)` receives each fresh row; the page merges it into its own state.
 */
export default function useProvisionSettle(id, status, onUpdate) {
  const unsettled = UNSETTLED.includes((status || '').toLowerCase());

  useEffect(() => {
    if (!id || !unsettled) return undefined;
    let cancelled = false;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await appServicesAPI.get(id, false);
        if (!cancelled) onUpdate(data);
        if (!UNSETTLED.includes((data.status || '').toLowerCase())) clearInterval(timer);
      } catch { /* keep polling — a blip must not end the watch */ }
      if (tries >= 30) clearInterval(timer);
    }, 2000);
    return () => { cancelled = true; clearInterval(timer); };
    // onUpdate is deliberately not a dependency: pages pass an inline setter, and
    // restarting the poll on every render would reset the bound.
  }, [id, unsettled]); // eslint-disable-line react-hooks/exhaustive-deps
}
